/**
 * AdminTable — Reusable MUI table with server-side pagination, sorting,
 * search, resizable columns, and persistent preferences.
 *
 * Props:
 *   tableId       - Unique ID for preference persistence (e.g., "admin_users")
 *   columns       - Array of { id, label, minWidth?, render?, sortable?, defaultWidth? }
 *   fetchData     - async (params) => { rows, total } — server-side data fetcher
 *   filters       - Optional array of { id, label, options: [{ value, label }] }
 *   actions       - Optional (row) => ReactNode — per-row action buttons
 *   onRowClick    - Optional (row) => void
 *   selectable    - Boolean, show checkboxes
 *   bulkActions   - Optional array of { label, color, onClick: (selectedIds) => void }
 *   refreshKey    - Change to trigger a refetch
 *   defaultSort   - { field, dir } — initial sort
 *   defaultRowsPerPage - Number
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel, Paper, TextField, Box, Typography,
  LinearProgress, Checkbox, Button, FormControl, InputLabel, Select,
  MenuItem, Chip, IconButton, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import apiClient from '../services/apiClient';

export default function AdminTable({
  tableId,
  columns,
  fetchData,
  filters = [],
  actions,
  onRowClick,
  selectable = false,
  bulkActions = [],
  refreshKey,
  defaultSort = { field: 'created_at', dir: 'desc' },
  defaultRowsPerPage = 25,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [sortField, setSortField] = useState(defaultSort.field);
  const [sortDir, setSortDir] = useState(defaultSort.dir);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [selected, setSelected] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  const debounceRef = useRef(null);
  const resizeRef = useRef(null);

  // Load preferences
  useEffect(() => {
    if (!tableId) { setPrefsLoaded(true); return; }
    apiClient.get(`/api/admin/table-preferences/${tableId}`)
      .then(data => {
        if (data.preferences) {
          const p = data.preferences;
          if (p.sortField) setSortField(p.sortField);
          if (p.sortDir) setSortDir(p.sortDir);
          if (p.rowsPerPage) setRowsPerPage(p.rowsPerPage);
          if (p.columnWidths) setColumnWidths(p.columnWidths);
        }
      })
      .catch(() => {})
      .finally(() => setPrefsLoaded(true));
  }, [tableId]);

  // Save preferences (debounced)
  const savePrefs = useCallback(() => {
    if (!tableId) return;
    clearTimeout(resizeRef.current);
    resizeRef.current = setTimeout(() => {
      apiClient.put(`/api/admin/table-preferences/${tableId}`, {
        preferences: { sortField, sortDir, rowsPerPage, columnWidths }
      }).catch(() => {});
    }, 1000);
  }, [tableId, sortField, sortDir, rowsPerPage, columnWidths]);

  useEffect(() => { if (prefsLoaded) savePrefs(); }, [sortField, sortDir, rowsPerPage, columnWidths, savePrefs, prefsLoaded]);

  // Fetch data
  const loadData = useCallback(async () => {
    if (!prefsLoaded) return;
    setLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_field: sortField,
        sort_dir: sortDir,
        search: debouncedSearch || undefined,
        ...filterValues,
      };
      const result = await fetchData(params);
      setRows(result.rows || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('AdminTable fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortField, sortDir, debouncedSearch, filterValues, fetchData, prefsLoaded]);

  useEffect(() => { loadData(); }, [loadData, refreshKey]);

  // Search debounce
  function handleSearchChange(value) {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 400);
  }

  function handleSort(field) {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(0);
  }

  function handleFilterChange(filterId, value) {
    setFilterValues(prev => ({ ...prev, [filterId]: value || undefined }));
    setPage(0);
  }

  // Selection
  const allSelected = rows.length > 0 && selected.length === rows.length;
  function toggleSelectAll() {
    setSelected(allSelected ? [] : rows.map(r => r.id));
  }
  function toggleSelect(id) {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  // Column resize
  function handleColumnResize(colId, startX, startWidth) {
    function onMove(e) {
      const newWidth = Math.max(60, startWidth + (e.clientX - startX));
      setColumnWidths(prev => ({ ...prev, [colId]: newWidth }));
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  return (
    <Box>
      {/* Toolbar: search + filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" /> }}
        />
        {filters.map(f => (
          <FormControl key={f.id} size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{f.label}</InputLabel>
            <Select
              value={filterValues[f.id] || ''}
              label={f.label}
              onChange={e => handleFilterChange(f.id, e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {f.options.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">{total} total</Typography>
      </Box>

      {/* Bulk actions bar */}
      {selectable && selected.length > 0 && (
        <Box sx={{ mb: 1, p: 1, bgcolor: 'action.selected', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {selected.length} selected
          </Typography>
          {bulkActions.map(ba => (
            <Button
              key={ba.label}
              size="small"
              variant="outlined"
              color={ba.color || 'primary'}
              onClick={() => { ba.onClick(selected); setSelected([]); }}
            >
              {ba.label}
            </Button>
          ))}
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selected.length > 0 && !allSelected}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
              )}
              {columns.map(col => (
                <TableCell
                  key={col.id}
                  sx={{
                    position: 'relative',
                    minWidth: col.minWidth || 80,
                    width: columnWidths[col.id] || col.defaultWidth || undefined,
                    userSelect: 'none',
                  }}
                  sortDirection={sortField === col.id ? sortDir : false}
                >
                  {col.sortable !== false ? (
                    <TableSortLabel
                      active={sortField === col.id}
                      direction={sortField === col.id ? sortDir : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      <strong>{col.label}</strong>
                    </TableSortLabel>
                  ) : (
                    <strong>{col.label}</strong>
                  )}
                  {/* Resize handle */}
                  <Box
                    sx={{
                      position: 'absolute', right: 0, top: 0, bottom: 0, width: 6,
                      cursor: 'col-resize', '&:hover': { bgcolor: 'divider' },
                    }}
                    onMouseDown={e => {
                      e.preventDefault();
                      const cell = e.target.parentElement;
                      handleColumnResize(col.id, e.clientX, cell.offsetWidth);
                    }}
                  />
                </TableCell>
              ))}
              {actions && <TableCell><strong>Actions</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}
                  sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}
                >
                  No results found
                </TableCell>
              </TableRow>
            )}
            {rows.map(row => (
              <TableRow
                key={row.id}
                hover
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                selected={selected.includes(row.id)}
              >
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.includes(row.id)}
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </TableCell>
                )}
                {columns.map(col => (
                  <TableCell key={col.id} sx={{ fontSize: '0.8rem' }}>
                    {col.render ? col.render(row) : row[col.id]}
                  </TableCell>
                ))}
                {actions && (
                  <TableCell onClick={e => e.stopPropagation()}>
                    {actions(row)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
}
